import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Stage, Layer, Rect, Line, Text, Circle, Arrow, Image as KonvaImage } from 'react-konva';
import { 
  ArrowLeft, Ruler, Pencil, Tag, Upload, Sparkles, Home as HomeIcon, 
  Check, X, Eraser, Undo, ChevronLeft, ChevronRight, Square, DoorOpen,
  Grid3x3, Layers
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';

const ROOM_CATEGORIES = {
  'Rooms': [
    'Master Bedroom', 'Bedroom', 'Children Bedroom',
    'Living Room', 'Dining Room', 'Study Room', 'Guest Room',
    'Kitchen', 'Store Room', 'Passage', 'Terrace', 'Garage', 'Balcony'
  ],
  'Sacred & Prayer': [
    'Pooja Room', 'Mandir', 'Prayer Space', 'Meditation Room'
  ],
  'Bathrooms & Toilets': [
    'Toilet', 'Bathroom', 'Attached Toilet', 'Common Toilet'
  ],
  'Entrances & Exits': [
    'Main Entrance', 'Main Door', 'Back Door', 'Side Entrance', 'Staircase'
  ],
  'Water Elements': [
    'Underground Water Tank', 'Underground Water Storage', 'Tubewell', 'Borewell',
    'Overhead Water Tank', 'Sump', 'Well', 'Water Pump'
  ],
  'Kitchen Elements': [
    'Kitchen Sink', 'Gas Stove', 'Kitchen Platform', 'Chimney'
  ],
  'Electrical & Storage': [
    'Inverter', 'Inverter Batteries', 'Electrical Panel', 'Generator',
    'Solar Panel', 'DB Box'
  ],
  'Open & External': [
    'Open Space', 'Courtyard', 'Garden', 'Lawn', 'Car Parking',
    'Septic Tank', 'Drainage'
  ]
};

const SHAPES_LIBRARY = [
  { id: 'door', name: 'Door', icon: DoorOpen, color: '#8B4513' },
  { id: 'window', name: 'Window', icon: Square, color: '#87CEEB' },
  { id: 'toilet', name: 'Toilet', icon: Grid3x3, color: '#4B5563' },
  { id: 'stairs', name: 'Stairs', icon: Layers, color: '#9CA3AF' }
];

function VastuModern() {
  const [step, setStep] = useState(1);
  const [plotSize, setPlotSize] = useState({ width: 40, length: 60, unit: 'ft' });
  const [currentFloor, setCurrentFloor] = useState(1);
  const [floors, setFloors] = useState({ 1: { lines: [], rooms: [], shapes: [] } });
  const [drawMode, setDrawMode] = useState('draw'); // draw, erase, label, shape
  const [selectedShape, setSelectedShape] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentLine, setCurrentLine] = useState([]);
  const [labelPopup, setLabelPopup] = useState(null);
  const [images, setImages] = useState({ interior: [], exterior: [] });
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  
  const navigate = useNavigate();
  const stageRef = useRef(null);

  // Canvas dimensions - maintain aspect ratio
  const maxCanvasWidth = 800;
  const maxCanvasHeight = 600;
  const aspectRatio = plotSize.width / plotSize.length;
  
  let canvasWidth, canvasHeight;
  if (aspectRatio > maxCanvasWidth / maxCanvasHeight) {
    canvasWidth = maxCanvasWidth;
    canvasHeight = maxCanvasWidth / aspectRatio;
  } else {
    canvasHeight = maxCanvasHeight;
    canvasWidth = maxCanvasHeight * aspectRatio;
  }

  const currentFloorData = floors[currentFloor] || { lines: [], rooms: [], shapes: [] };

  const totalSqFt = plotSize.unit === 'ft' ? 
    plotSize.width * plotSize.length : 
    (plotSize.width * plotSize.length * 10.764).toFixed(2);


  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  const handlePlotSubmit = () => {
    if (plotSize.width > 0 && plotSize.length > 0) {
      setStep(2);
    }
  };

  const handleMouseDown = (e) => {
    if (drawMode !== 'draw' && drawMode !== 'erase') return;
    
    const pos = e.target.getStage().getPointerPosition();
    setIsDrawing(true);
    setCurrentLine([pos.x, pos.y]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    
    const pos = e.target.getStage().getPointerPosition();
    
    if (drawMode === 'draw') {
      // Snap to grid
      const snappedX = Math.round(pos.x / 20) * 20;
      const snappedY = Math.round(pos.y / 20) * 20;
      setCurrentLine([...currentLine, snappedX, snappedY]);
    } else if (drawMode === 'erase') {
      // Erase lines near cursor
      const newLines = currentFloorData.lines.filter(line => {
        return !line.points.some((point, i) => {
          if (i % 2 === 0) {
            const x = point;
            const y = line.points[i + 1];
            const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));
            return distance < 20;
          }
          return false;
        });
      });
      updateFloorData({ lines: newLines });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    if (drawMode === 'draw' && currentLine.length >= 4) {
      updateFloorData({
        lines: [...currentFloorData.lines, { points: currentLine }]
      });
    }
    
    setCurrentLine([]);
  };

  const handleCanvasClick = (e) => {
    if (drawMode === 'label') {
      const pos = e.target.getStage().getPointerPosition();
      setLabelPopup({ x: pos.x, y: pos.y, screenX: e.evt.clientX, screenY: e.evt.clientY });
    }
  };

  const handleRoomSelect = (roomType) => {
    if (!labelPopup) return;
    
    const direction = calculateDirection(labelPopup.x, labelPopup.y);
    const newRoom = {
      id: Date.now(),
      x: labelPopup.x,
      y: labelPopup.y,
      label: roomType,
      direction
    };
    
    updateFloorData({
      rooms: [...currentFloorData.rooms, newRoom]
    });
    
    setLabelPopup(null);
    // Keep in label mode so user can add more rooms without clicking button again
    // setDrawMode('draw'); // Removed this line
  };

  const calculateDirection = (x, y) => {
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    const dx = x - centerX;
    const dy = centerY - y; // Flip Y for correct orientation
    
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    
    // 8 directions (North at top = 90°)
    if (angle >= 67.5 && angle < 112.5) return 'N';
    if (angle >= 22.5 && angle < 67.5) return 'NE';
    if (angle >= -22.5 && angle < 22.5) return 'E';
    if (angle >= -67.5 && angle < -22.5) return 'SE';
    if (angle >= -112.5 && angle < -67.5) return 'S';
    if (angle >= -157.5 && angle < -112.5) return 'SW';
    if (angle >= 112.5 && angle < 157.5) return 'NW';
    return 'W';
  };

  const updateFloorData = (updates) => {
    const newFloorData = { ...currentFloorData, ...updates };
    const newFloors = {
      ...floors,
      [currentFloor]: newFloorData
    };
    
    // Save to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ floors: newFloors, currentFloor });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    setFloors(newFloors);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setFloors(prevState.floors);
      setCurrentFloor(prevState.currentFloor);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setFloors(nextState.floors);
      setCurrentFloor(nextState.currentFloor);
      setHistoryIndex(historyIndex + 1);
    }
  };

  const handleRoomDragEnd = (e, roomId) => {
    const room = currentFloorData.rooms.find(r => r.id === roomId);
    if (!room) return;
    
    const newX = e.target.x();
    const newY = e.target.y();
    const direction = calculateDirection(newX, newY);
    
    const updatedRooms = currentFloorData.rooms.map(r =>
      r.id === roomId ? { ...r, x: newX, y: newY, direction } : r
    );
    
    updateFloorData({ rooms: updatedRooms });
  };

  const handleDeleteRoom = (roomId) => {
    const updatedRooms = currentFloorData.rooms.filter(r => r.id !== roomId);
    updateFloorData({ rooms: updatedRooms });
  };

  const addFloor = () => {
    const newFloorNum = Math.max(...Object.keys(floors).map(Number)) + 1;
    setFloors({
      ...floors,
      [newFloorNum]: { lines: [], rooms: [], shapes: [] }
    });
    setCurrentFloor(newFloorNum);
  };

  const handleAnalyze = async () => {
    setLoading(true);
    
    try {
      // Collect all rooms from all floors
      const allRooms = [];
      Object.entries(floors).forEach(([floorNum, floorData]) => {
        floorData.rooms.forEach(room => {
          allRooms.push({
            room_type: room.label,
            direction: room.direction,
            notes: `Floor ${floorNum}, Position (${Math.round(room.x)}, ${Math.round(room.y)})`
          });
        });
      });

      if (allRooms.length === 0) {
        alert('Please label at least one room before analyzing');
        setLoading(false);
        return;
      }

      const response = await axios.post('/api/vastu/analyze-manual', {
        room_placements: allRooms
      });
      
      setAnalysis(response.data);
      setStep(5);
    } catch (err) {
      console.error('Analysis error:', err);
      alert('Analysis failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps: getInteriorProps, getInputProps: getInteriorInput } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    onDrop: (files) => setImages({ ...images, interior: [...images.interior, ...files] })
  });

  const { getRootProps: getExteriorProps, getInputProps: getExteriorInput } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    onDrop: (files) => setImages({ ...images, exterior: [...images.exterior, ...files] })
  });

  const getSeverityColor = (severity) => {
    if (severity === 'critical') return '#DC2626';
    if (severity === 'major') return '#FB923C';
    return '#F59E0B';
  };

  const goToStep = (newStep) => {
    if (newStep >= 1 && newStep <= 5) {
      setStep(newStep);
    }
  };

  return (
    <div className="page" style={{ background: '#0A0F2A', minHeight: '100vh' }}>
      <div className="container" style={{ maxWidth: '1200px' }}>
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost" style={{ marginBottom: '2rem' }}>
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', 
            marginBottom: '1rem',
            background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Modern Vastu Analysis
          </h1>
          <p style={{ color: '#9CA3AF', fontSize: '1.25rem' }}>Draw your space, let AI analyze it</p>
        </motion.div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '3rem' }}>
          {[
            { num: 1, label: 'Plot Size' },
            { num: 2, label: 'Draw Plan' },
            { num: 3, label: 'Label Rooms' },
            { num: 4, label: 'Add Photos' },
            { num: 5, label: 'Insights' }
          ].map((s) => (
            <button
              key={s.num}
              onClick={() => s.num < step && goToStep(s.num)}
              style={{
                background: 'none',
                border: 'none',
                cursor: s.num < step ? 'pointer' : 'default',
                textAlign: 'center'
              }}
              disabled={s.num > step}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: step >= s.num ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : '#1E2139',
                border: step === s.num ? '3px solid #10B981' : '2px solid #374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 0.5rem',
                boxShadow: step === s.num ? '0 0 20px rgba(16, 185, 129, 0.5)' : 'none'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: step >= s.num ? '#FFF' : '#9CA3AF' }}>
                  {s.num}
                </span>
              </div>
              <p style={{ fontSize: '0.8125rem', color: step >= s.num ? '#10B981' : '#6B7280' }}>{s.label}</p>
            </button>
          ))}
        </div>

        {/* Step 1: Plot Size */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', textAlign: 'center' }}>Let's Understand Your Space</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
              <div className="form-group">
                <label className="form-label">Width</label>
                <input
                  type="number"
                  className="form-input"
                  value={plotSize.width}
                  onChange={(e) => setPlotSize({ ...plotSize, width: parseFloat(e.target.value) || 0 })}
                  placeholder="40"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Length</label>
                <input
                  type="number"
                  className="form-input"
                  value={plotSize.length}
                  onChange={(e) => setPlotSize({ ...plotSize, length: parseFloat(e.target.value) || 0 })}
                  placeholder="60"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Unit</label>
                <select
                  className="form-input"
                  value={plotSize.unit}
                  onChange={(e) => setPlotSize({ ...plotSize, unit: e.target.value })}
                >
                  <option value="ft">Feet (ft)</option>
                  <option value="m">Meters (m)</option>
                </select>
              </div>
            </div>

            <div style={{ textAlign: 'center', padding: '2rem', background: '#1E2139', borderRadius: '12px', marginBottom: '2rem' }}>
              <p style={{ fontSize: '2.5rem', fontWeight: 700, color: '#10B981', marginBottom: '0.5rem' }}>
                {plotSize.width} × {plotSize.length} {plotSize.unit}
              </p>
              <p style={{ fontSize: '1.25rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                = {totalSqFt} sq ft
              </p>
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>Total plot area</p>
            </div>

            <button
              onClick={handlePlotSubmit}
              className="btn btn-primary"
              disabled={!plotSize.width || !plotSize.length}
              style={{ width: '100%', fontSize: '1.125rem', padding: '1rem' }}
            >
              Generate Directional Grid & Start Drawing
            </button>
          </motion.div>
        )}

        {/* Step 2 & 3: Draw and Label (Combined) */}
        {(step === 2 || step === 3) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card" style={{ marginBottom: '2rem' }}>
              {/* Floor Selector */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #2D3748' }}>
                <div>
                  <h2 style={{ fontSize: '1.75rem' }}>
                    {step === 2 ? 'Draw Your Floor Plan' : 'Label Your Rooms'}
                  </h2>
                  <p style={{ color: '#9CA3AF', marginTop: '0.5rem' }}>
                    {step === 2 ? 'Draw walls - click to start/stop points. Lines auto-straighten.' : 'Click on canvas to add room labels'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>Floor:</span>
                  {Object.keys(floors).map(floorNum => (
                    <button
                      key={floorNum}
                      onClick={() => setCurrentFloor(parseInt(floorNum))}
                      className={currentFloor === parseInt(floorNum) ? 'btn btn-primary' : 'btn btn-ghost'}
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      {floorNum}
                    </button>
                  ))}
                  <button
                    onClick={addFloor}
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    + Floor
                  </button>
                </div>
              </div>

              {/* Drawing Tools */}
              {step === 2 && (
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setDrawMode('draw')}
                    className={drawMode === 'draw' ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Pencil size={18} />
                    Draw
                  </button>
                  <button
                    onClick={() => setDrawMode('erase')}
                    className={drawMode === 'erase' ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Eraser size={18} />
                    Erase
                  </button>
                  <button
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    className="btn btn-ghost"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: historyIndex <= 0 ? 0.5 : 1 }}
                    title="Undo (Ctrl+Z)"
                  >
                    <Undo size={18} />
                    Undo
                  </button>
                  <button
                    onClick={() => updateFloorData({ lines: [] })}
                    className="btn btn-ghost"
                  >
                    Clear All
                  </button>
                  
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                    <span style={{ color: '#9CA3AF', fontSize: '0.875rem', alignSelf: 'center' }}>Shapes:</span>
                    {SHAPES_LIBRARY.map(shape => (
                      <button
                        key={shape.id}
                        onClick={() => { setSelectedShape(shape.id); setDrawMode('shape'); }}
                        className={selectedShape === shape.id ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ padding: '0.5rem', display: 'flex', alignItems: 'center' }}
                        title={shape.name}
                      >
                        <shape.icon size={18} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={() => setDrawMode(drawMode === 'label' ? 'draw' : 'label')}
                      className={drawMode === 'label' ? 'btn btn-primary' : 'btn btn-secondary'}
                      style={{ position: 'relative' }}
                    >
                      <Tag size={18} style={{ marginRight: '0.5rem' }} />
                      {drawMode === 'label' ? '✓ Labeling Mode Active - Click Canvas' : 'Start Labeling Rooms'}
                    </button>
                    {drawMode === 'label' && (
                      <span style={{ color: '#10B981', fontSize: '0.875rem', animation: 'pulse 2s infinite' }}>
                        💡 Click anywhere on canvas to place room markers
                      </span>
                    )}
                    <button
                      onClick={handleUndo}
                      disabled={historyIndex <= 0}
                      className="btn btn-ghost"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: historyIndex <= 0 ? 0.5 : 1 }}
                      title="Undo last action"
                    >
                      <Undo size={18} />
                      Undo
                    </button>
                    {drawMode === 'label' && (
                      <button
                        onClick={() => setDrawMode('draw')}
                        className="btn btn-ghost"
                        style={{ marginLeft: 'auto' }}
                      >
                        Exit Labeling Mode
                      </button>
                    )}
                  </div>
                  
                  {currentFloorData.rooms.length > 0 && (
                    <div style={{ padding: '1rem', background: '#1E2139', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                        Floor {currentFloor} - Labeled Rooms ({currentFloorData.rooms.length}):
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                        💡 Tip: Drag room markers to reposition. Double-click to delete.
                      </p>
                      {currentFloorData.rooms.map((room, i) => (
                        <div key={room.id} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          padding: '0.5rem 0', 
                          borderBottom: i < currentFloorData.rooms.length - 1 ? '1px solid #2D3748' : 'none'
                        }}>
                          <span>{room.label}</span>
                          <span style={{ color: '#10B981', fontWeight: 600 }}>{room.direction}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Canvas */}
              <div style={{ position: 'relative', background: '#1A1B2E', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
                <Stage
                  width={canvasWidth}
                  height={canvasHeight}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onClick={handleCanvasClick}
                  ref={stageRef}
                  style={{ 
                    border: '2px solid #2D3748', 
                    borderRadius: '8px', 
                    background: '#0F1419',
                    cursor: drawMode === 'draw' ? 'crosshair' : drawMode === 'erase' ? 'not-allowed' : drawMode === 'label' ? 'pointer' : 'default'
                  }}
                >
                  <Layer>
                    {/* Border */}
                    <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} stroke="#374151" strokeWidth={2} />
                    
                    {/* Grid */}
                    {Array.from({ length: Math.floor(canvasWidth / 20) }).map((_, i) => (
                      <Line
                        key={`v-${i}`}
                        points={[i * 20, 0, i * 20, canvasHeight]}
                        stroke="#2D3748"
                        strokeWidth={1}
                        dash={[3, 3]}
                        opacity={0.3}
                      />
                    ))}
                    {Array.from({ length: Math.floor(canvasHeight / 20) }).map((_, i) => (
                      <Line
                        key={`h-${i}`}
                        points={[0, i * 20, canvasWidth, i * 20]}
                        stroke="#2D3748"
                        strokeWidth={1}
                        dash={[3, 3]}
                        opacity={0.3}
                      />
                    ))}

                    {/* Directional Arrows */}
                    <Arrow points={[canvasWidth / 2, 50, canvasWidth / 2, 20]} stroke="#10B981" fill="#10B981" strokeWidth={3} pointerLength={12} pointerWidth={12} />
                    <Text x={canvasWidth / 2 - 35} y={60} text="NORTH ↑" fontSize={16} fill="#10B981" fontStyle="bold" />
                    <Text x={canvasWidth - 60} y={canvasHeight / 2 - 10} text="EAST →" fontSize={14} fill="#9CA3AF" />
                    <Text x={10} y={canvasHeight / 2 - 10} text="← WEST" fontSize={14} fill="#9CA3AF" />
                    <Text x={canvasWidth / 2 - 30} y={canvasHeight - 30} text="SOUTH ↓" fontSize={14} fill="#9CA3AF" />

                    {/* User Lines */}
                    {currentFloorData.lines.map((line, i) => (
                      <Line
                        key={i}
                        points={line.points}
                        stroke="#00D9FF"
                        strokeWidth={3}
                        lineCap="round"
                        lineJoin="round"
                      />
                    ))}

                    {/* Current drawing line */}
                    {isDrawing && currentLine.length >= 2 && (
                      <Line
                        points={currentLine}
                        stroke="#00D9FF"
                        strokeWidth={3}
                        lineCap="round"
                        opacity={0.6}
                      />
                    )}

                    {/* Room Labels - Draggable */}
                    {currentFloorData.rooms.map((room) => (
                      <React.Fragment key={room.id}>
                        <Circle 
                          x={room.x} 
                          y={room.y} 
                          radius={12} 
                          fill="#10B981" 
                          stroke="#FFF" 
                          strokeWidth={2}
                          draggable={step === 3}
                          onDragEnd={(e) => handleRoomDragEnd(e, room.id)}
                          onMouseEnter={(e) => {
                            const container = e.target.getStage().container();
                            container.style.cursor = 'move';
                          }}
                          onMouseLeave={(e) => {
                            const container = e.target.getStage().container();
                            container.style.cursor = drawMode === 'label' ? 'pointer' : 'default';
                          }}
                          onClick={(e) => {
                            if (step === 3 && drawMode !== 'label') {
                              e.cancelBubble = true;
                              setSelectedRoomId(room.id);
                            }
                          }}
                          onDblClick={(e) => {
                            e.cancelBubble = true;
                            if (window.confirm(`Delete "${room.label}"?`)) {
                              handleDeleteRoom(room.id);
                            }
                          }}
                        />
                        <Text 
                          x={room.x + 18} 
                          y={room.y - 28} 
                          text={room.label} 
                          fontSize={13} 
                          fill="#F9FAFB" 
                          fontStyle="bold"
                          padding={4}
                          cornerRadius={4}
                          listening={false}
                        />
                        <Text 
                          x={room.x + 18} 
                          y={room.y - 8} 
                          text={`(${room.direction})`} 
                          fontSize={11} 
                          fill="#10B981"
                          listening={false}
                        />
                      </React.Fragment>
                    ))}
                  </Layer>
                </Stage>

                {/* Room Label Popup */}
                <AnimatePresence>
                  {labelPopup && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: '#151827',
                        padding: '1.5rem',
                        borderRadius: '16px',
                        border: '2px solid #10B981',
                        zIndex: 100,
                        minWidth: '400px',
                        maxWidth: '500px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem' }}>What is this space?</h3>
                        <button
                          onClick={() => setLabelPopup(null)}
                          style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer' }}
                        >
                          <X size={24} />
                        </button>
                      </div>

                      {Object.entries(ROOM_CATEGORIES).map(([category, items]) => (
                        <div key={category} style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ 
                            fontSize: '0.9375rem', 
                            color: '#10B981', 
                            marginBottom: '0.75rem',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            letterSpacing: '0.05em'
                          }}>
                            {category}
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                            {items.map(room => (
                              <button
                                key={room}
                                onClick={() => handleRoomSelect(room)}
                                className="btn btn-secondary"
                                style={{ 
                                  textAlign: 'left', 
                                  padding: '0.75rem 1rem',
                                  justifyContent: 'flex-start',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {room}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
                {step > 1 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="btn btn-ghost"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <ChevronLeft size={20} />
                    Back
                  </button>
                )}
                <button
                  onClick={() => setStep(step + 1)}
                  className="btn btn-primary"
                  disabled={step === 2 && currentFloorData.lines.length === 0}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  {step === 2 ? 'Next: Label Rooms' : 'Next: Add Photos'}
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Photos */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card">
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>Add Photos (Optional)</h2>
              <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Upload photos for deeper AI analysis</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Interior Photos</h3>
                  <div {...getInteriorProps()} style={{
                    padding: '2rem', border: '2px dashed #374151', borderRadius: '12px',
                    textAlign: 'center', cursor: 'pointer', background: '#1E2139'
                  }}>
                    <input {...getInteriorInput()} />
                    <Upload size={32} color="#9CA3AF" style={{ margin: '0 auto 1rem' }} />
                    <p style={{ color: '#9CA3AF' }}>Drag photos or click</p>
                  </div>
                  {images.interior.length > 0 && <p style={{ marginTop: '0.5rem', color: '#10B981' }}>{images.interior.length} added</p>}
                </div>

                <div>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Exterior Photos</h3>
                  <div {...getExteriorProps()} style={{
                    padding: '2rem', border: '2px dashed #374151', borderRadius: '12px',
                    textAlign: 'center', cursor: 'pointer', background: '#1E2139'
                  }}>
                    <input {...getExteriorInput()} />
                    <Upload size={32} color="#9CA3AF" style={{ margin: '0 auto 1rem' }} />
                    <p style={{ color: '#9CA3AF' }}>Drag photos or click</p>
                  </div>
                  {images.exterior.length > 0 && <p style={{ marginTop: '0.5rem', color: '#10B981' }}>{images.exterior.length} added</p>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={() => setStep(3)} className="btn btn-ghost">
                  <ChevronLeft size={20} style={{ marginRight: '0.5rem' }} />
                  Back
                </button>
                <button
                  onClick={handleAnalyze}
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Analyzing...' : 'Get Vastu Insights'}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 5: Results */}
        {step === 5 && analysis && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card" style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <HomeIcon size={48} color="#10B981" style={{ margin: '0 auto 1rem' }} />
              <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Your Vastu Analysis</h2>
              <p style={{ color: '#9CA3AF' }}>Based on {Object.values(floors).reduce((sum, f) => sum + f.rooms.length, 0)} room(s) across {Object.keys(floors).length} floor(s)</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginTop: '2rem' }}>
                <div style={{ padding: '1rem', background: '#1E2139', borderRadius: '12px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#FB923C' }}>
                    {analysis.analysis.total_issues}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Total Issues</p>
                </div>
                <div style={{ padding: '1rem', background: '#1E2139', borderRadius: '12px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#DC2626' }}>
                    {analysis.analysis.issues_by_severity.critical.length}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Critical</p>
                </div>
                <div style={{ padding: '1rem', background: '#1E2139', borderRadius: '12px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#FB923C' }}>
                    {analysis.analysis.issues_by_severity.major.length}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Major</p>
                </div>
                <div style={{ padding: '1rem', background: '#1E2139', borderRadius: '12px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#F59E0B' }}>
                    {analysis.analysis.issues_by_severity.moderate.length}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Moderate</p>
                </div>
              </div>
            </div>

            {analysis.analysis.detected_issues && analysis.analysis.detected_issues.length > 0 ? (
              <div>
                {analysis.analysis.detected_issues.map((issue, i) => (
                  <div key={i} className="card" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #2D3748' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{issue.problem}</h3>
                        <span className="badge" style={{ 
                          background: `rgba(${getSeverityColor(issue.severity) === '#DC2626' ? '220, 38, 38' : '251, 146, 60'}, 0.2)`,
                          color: getSeverityColor(issue.severity),
                          textTransform: 'uppercase',
                          padding: '0.5rem 1rem'
                        }}>
                          {issue.severity}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                        Deity Affected: {issue.deity_affected}
                      </p>
                      {issue.health_impact && (
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '0.75rem', 
                          background: 'rgba(220, 38, 38, 0.1)', 
                          borderRadius: '8px',
                          borderLeft: '3px solid #DC2626'
                        }}>
                          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#DC2626', marginBottom: '0.25rem' }}>
                            ⚕️ Health Impact:
                          </p>
                          <p style={{ fontSize: '0.875rem', color: '#F87171' }}>
                            {issue.health_impact}
                          </p>
                        </div>
                      )}
                    </div>

                    {issue.effects && issue.effects.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#FB923C' }}>You may experience:</h4>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {issue.effects.map((effect, j) => (
                            <span key={j} className="badge badge-warning">{effect}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Show remedies for this issue */}
                    {analysis.analysis.remedies?.immediate && (
                      <div>
                        {(() => {
                          const matchingRemedies = analysis.analysis.remedies.immediate
                            .filter(r => r.issue.includes(issue.room))
                            .slice(0, 1); // Only show first matching remedy to avoid duplicates
                          
                          if (matchingRemedies.length === 0) return null;
                          
                          return matchingRemedies.map((remedy, idx) => (
                            <div key={idx}>
                              <h4 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#10B981' }}>
                                Instant Remedies:
                              </h4>
                              <ul style={{ listStyle: 'none', padding: 0 }}>
                                {remedy.remedies.map((r, j) => (
                                  <li key={j} style={{
                                    padding: '0.75rem',
                                    background: 'rgba(16, 185, 129, 0.05)',
                                    borderRadius: '8px',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem'
                                  }}>
                                    <Check size={18} color="#10B981" style={{ flexShrink: 0, marginTop: '2px' }} />
                                    <span style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>{r}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <Check size={64} color="#10B981" style={{ margin: '0 auto 1.5rem' }} />
                <h3 style={{ fontSize: '1.75rem', marginBottom: '1rem', color: '#10B981' }}>
                  Excellent Vastu Alignment!
                </h3>
                <p style={{ color: '#D1D5DB', fontSize: '1.125rem' }}>
                  No major doshas detected. Your space shows good Vastu harmony.
                </p>
              </div>
            )}

            <button
              onClick={() => { setStep(1); setFloors({ 1: { lines: [], rooms: [], shapes: [] } }); setCurrentFloor(1); setAnalysis(null); }}
              className="btn btn-secondary"
              style={{ marginTop: '2rem' }}
            >
              Analyze Another Space
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default VastuModern;
